/**
 * FF Article Parser - Detects and replaces product slider markers in article content
 *
 * Supported syntax:
 * - [products:id1,id2,id3] - Product IDs (modern syntax)
 * - [collection:handle] - Collection handle (modern syntax)
 * - [*id1,id2,id3*] - Product IDs (legacy syntax)
 * - {*collection_handle*} - Collection handle (legacy syntax)
 */

class FFArticleParser {
  constructor(container) {
    this.container = container;
    this.patterns = {
      productsModern: /\[products:([\s\S]+?)\]/g,
      collectionModern: /\[collection:([\s\S]+?)\]/g,
      productsLegacy: /\[\*([\s\S]+?)\*\]/g,
      collectionLegacy: /\{\*([\s\S]+?)\*\}/g
    };
  }

  /**
   * Parse the container content and replace markers with slider components
   */
  parse() {
    let newHtml = this.container.innerHTML;
    let hasChanges = false;

    const replaceMarker = (pattern, type) => {
      newHtml = newHtml.replace(pattern, (match, raw) => {
        const value = this.cleanValue(raw, type);
        if (!value) return match;
        hasChanges = true;
        return this.createSliderElement(type, value);
      });
    };

    replaceMarker(this.patterns.productsModern, 'products');
    replaceMarker(this.patterns.collectionModern, 'collection');
    replaceMarker(this.patterns.productsLegacy, 'products');
    replaceMarker(this.patterns.collectionLegacy, 'collection');

    if (hasChanges) {
      this.container.innerHTML = newHtml;
      this.initSliders();
    }
  }

  /**
   * Sanitize the captured marker value into a clean handle / ID list.
   * @param {string} raw - Raw capture (may contain HTML pasted by the RTE)
   * @param {string} type - 'products' or 'collection'
   * @returns {string} Clean value, or '' if nothing valid was found
   */
  cleanValue(raw, type) {
    const text = raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ');
    const isHandleOrId = (token) => /^[a-z0-9][a-z0-9-]*$/i.test(token);

    if (type === 'collection') {
      const handle = text.trim();
      return isHandleOrId(handle) ? handle : '';
    }

    return text
      .split(',')
      .map((token) => token.trim())
      .filter(isHandleOrId)
      .join(',');
  }

  /**
   * Create the slider HTML element
   * @param {string} type - 'products' or 'collection'
   * @param {string} value - Product IDs (comma-separated) or collection handle
   */
  createSliderElement(type, value) {
    if (type === 'products') {
      return `<ff-article-product-slider data-product-ids="${this.escapeHtml(value)}"></ff-article-product-slider>`;
    } else {
      return `<ff-article-product-slider data-collection="${this.escapeHtml(value)}"></ff-article-product-slider>`;
    }
  }

  /**
   * Initialize all slider components after DOM update
   */
  initSliders() {
    const sliders = this.container.querySelectorAll('ff-article-product-slider');
    sliders.forEach(slider => {
      if (!slider._initialized) {
        slider.init();
      }
    });
  }

  /**
   * Escape HTML special characters
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const articleContent = document.querySelector('.ff-article-content');
  if (articleContent) {
    const parser = new FFArticleParser(articleContent);
    parser.parse();
  }
});

// Export for external use
window.FFArticleParser = FFArticleParser;
